import * as Config from '@/Config';
import Router from '@/Router';
import Store from '@/Store';
import Vue from 'vue';

/**
 * Module-level flag to prevent duplicate Vue.use() calls
 *
 * @type boolean
 */
let trainerInstalled: boolean = false;

/**
 * Service for dynamically loading and initializing the ChalkySticks trainer
 * plugin. Handles the async import, Vue.use() registration, and ensures
 * the plugin is only installed once across the application.
 *
 * @author Matt Kenefick <matt@chalkysticks.com>
 * @package Service
 * @project ChalkySticks Web
 */
const TrainerLoader = {
	/**
	 * Dynamically import and install the ChalkySticks trainer plugin.
	 * Safe to call multiple times; only installs once.
	 *
	 * @return Promise<void>
	 */
	async initialize(): Promise<void> {
		if (trainerInstalled) {
			return;
		}

		const ChalkySticksTrainerModule = await import(
			/* webpackChunkName: "chalkysticks-trainer-runtime" */
			'@chalkysticks/vue2-trainer'
		);

		const ChalkySticksTrainer = (ChalkySticksTrainerModule as any).default ?? (ChalkySticksTrainerModule as any);

		Vue.use(ChalkySticksTrainer, {
			assets: {
				modelUrl: Config.Plugin.TRAINER_MODEL_URL,
			},
			environment: {
				default: Config.Plugin.TRAINER_ENVIRONMENT,
			},
			features: {
				trajectoryPrediction: true,
			},
			provide: {
				router: Router,
				store: Store,
			},
			rendering: {
				// adaptiveDpr: true,
				environmentMap: {
					enabled: true,
				},
				maxDprDesktop: Math.min(1.5, window.devicePixelRatio * 0.9), // 0.75 actually looks good on my windows machine
				maxDprMobile: Math.min(1, window.devicePixelRatio * 0.85), // 0.75 actually looks good on my windows machine
				rendererHigh: {
					antialias: false,
				},
				rendererLow: {
					antialias: false,
				},
				textureExtension: 'ktx2', // 'ktx2',
				textureSize: 1024, // because ios memory is a disaster
			},
			sdk: {
				api_url: Config.App?.SERVER_URL,
			},
			table: {
				appearance: {
					default: Config.Plugin.TRAINER_TABLE_APPEARANCE,
				},
			},
		});

		trainerInstalled = true;
	},

	/**
	 * Whether the trainer plugin has been installed
	 *
	 * @return boolean
	 */
	get isInstalled(): boolean {
		return trainerInstalled;
	},

	/**
	 * Reset the installation flag, allowing the trainer to be
	 * re-initialized. Useful for retry logic after a failed load.
	 *
	 * @return void
	 */
	reset(): void {
		trainerInstalled = false;
	},
};

export default TrainerLoader;
